import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Chip, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'

import type { LessonChatWidgetMeta } from '@/api/chat'
import { formatDateDMY } from '@/utils/date'

interface LessonChatWidgetCardProps {
  meta: LessonChatWidgetMeta
  onOpen?: (meta: LessonChatWidgetMeta) => void
}

/**
 * Плашка истории урока в ленте обычной переписки.
 *
 * Собрана как сообщение от системы: слева аватар с логотипом EasyA — там же,
 * где аватар собеседника, — а вместо пузыря карточка урока во всю оставшуюся
 * ширину. Серый пузырь по центру читался как реплика собеседника, хотя это
 * запись платформы.
 *
 * Заголовок и дата собираются здесь, а не приходят с сервера: одно и то же
 * сообщение читают обе стороны, и локаль у них может быть разная.
 */
export const LessonChatWidgetCard = memo(function LessonChatWidgetCard({
  meta,
  onOpen,
}: LessonChatWidgetCardProps) {
  const { t } = useTranslation()
  const isClickable = typeof onOpen === 'function'

  return (
    // Отступы больше, чем между сообщениями: плашка — веха в переписке, и
    // вплотную к соседним репликам она читалась бы как одна из них
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', my: 2.5, gap: 0.5 }}>
      {/* Место и размер — как у аватара автора в MessageBubble, чтобы плашка
          встала в ленту по сетке остальных сообщений. Но не Avatar: тот режет
          картинку в круг и срезает логотипу углы */}
      <Box
        component="img"
        src="/icon/android-chrome-192x192.png"
        alt="EASYA"
        onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
          event.currentTarget.src = '/icon/apple-touch-icon.png'
        }}
        sx={{ width: 32, height: 32, mt: 0.25, mr: 0.5, flexShrink: 0, objectFit: 'contain' }}
      />
      <Box
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onClick={isClickable ? () => onOpen?.(meta) : undefined}
        onKeyDown={
          isClickable
            ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  onOpen?.(meta)
                }
              }
            : undefined
        }
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flex: 1,
          minWidth: 0,
          px: 1.5,
          py: 1,
          border: 1,
          borderColor: alpha(theme.palette.primary.main, 0.35),
          borderRadius: 2,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.16)} 0%, ${alpha(
            theme.palette.primary.main,
            0.03
          )} 100%)`,
          cursor: isClickable ? 'pointer' : 'default',
          transition: theme.transitions.create(['border-color', 'background']),
          ...(isClickable && {
            '&:hover': {
              borderColor: theme.palette.primary.main,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.24
              )} 0%, ${alpha(theme.palette.primary.main, 0.06)} 100%)`,
            },
          }),
        })}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {t('chat.lessonWidget.title', {
              id: meta.lesson_id,
              date: formatDateDMY(meta.lesson_date),
            })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('chat.lessonWidget.hint')}
          </Typography>
        </Box>
        {meta.lesson_status === 'canceled' && (
          <Chip size="small" label={t('chat.lessonWidget.canceled')} sx={{ flexShrink: 0 }} />
        )}
      </Box>
    </Box>
  )
})
